import { apiClient, ApiError } from '$lib/api/client';

/**
 * The customer's own named sets of companies and people.
 *
 * # This is not `api/lists.ts`
 *
 * There are two unrelated things called a list, and the paths are deliberately
 * far apart because of it. `api/lists.ts` is the *uploaded* list — a
 * spreadsheet of the customer's existing customers, authorised by a code, used
 * to subtract from a selection, swept after a retention period. This one is
 * companies and people the customer picked out, owned by an account, kept
 * until deleted.
 *
 * `/deep-iq/lists/*` is that one. `/lists/*` is this one. Nothing here should
 * ever be named `list*` without the `saved` — see docs/refactor/saved-lists.md.
 *
 * # Records are keyed to survive a rebuild
 *
 * Worth knowing when reading the shapes below: a record carries a *snapshot* of
 * the company name, the person's name and their role, taken when it was added
 * and never updated. That is why a list can draw itself without joining the
 * corpus, and why somebody who has since left still appears in the list they
 * were put in. `in_corpus` says whether there is still a live row behind them.
 */

/** Who may see a list. */
export type ListScope = 'user' | 'organisation';

/** A list, without what it holds. */
export interface SavedList {
  id: string;
  name: string;
  scope: ListScope;

  /**
   * Whether the signed-in account made this one.
   *
   * On a shared list the app otherwise cannot tell its own from a colleague's,
   * and the owner identifier is not something to publish to the rest of the
   * organisation.
   */
  mine: boolean;

  entries: number;
  created_at: string;
  updated_at: string;
}

/** One record in a list. */
export interface SavedListRecord {
  /** Identifies the record for removal. Scoped to its list, not global. */
  id: string;

  company_id: string;
  company_name?: string;

  /** "company" or "contact", so a client groups without inferring it. */
  kind: 'company' | 'contact';

  contact_id?: string;
  name?: string;
  role?: string;

  /**
   * False for a person the last contact rebuild no longer found.
   *
   * The record is intact — it is keyed on who they are and carries its own
   * snapshot — but there is no live row behind it. It means "do not offer to
   * open their profile", not "this record is broken", and the screen must not
   * present it as an error. Always true for a company record.
   */
  in_corpus: boolean;

  added_at: string;
}

/** A list with what it holds. */
export interface SavedListDetail extends SavedList {
  records: SavedListRecord[];
}

/** What to put in a list: a company, or a person at one. */
export interface SavedListRecordInput {
  company_id?: string;
  contact_id?: string;
}

/** How an add ended. */
export interface AddRecordsOutcome {
  /** How many records the list gained. */
  added: number;

  /** How many were asked for. Lower than `added` never; higher often. */
  requested: number;
}

/**
 * A list request the server refused, with the code it refused under.
 *
 * The codes are worth keeping: `NO_ORGANISATION` is a seatless account asking
 * for a shared list, `LIST_NAME_TOO_LONG` is fixable by the customer, and
 * `LIST_NOT_FOUND` is also what somebody else's list answers. A bare "failed"
 * would throw all three away.
 */
export class SavedListRefused extends Error {
  constructor(
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'SavedListRefused';
  }
}

/** The longest name the server will take, so the form can say so first. */
export const maxListNameLength = 120;

/**
 * How many records one request may carry.
 *
 * The server's cap (`maxListRecordsPerRequest`). Exported because the batching
 * below is the reason no caller has to know it, and a test that did not know
 * it could not check the batching.
 */
export const maxRecordsPerRequest = 1000;

/** Every list the signed-in account may see. */
export async function fetchSavedLists(): Promise<SavedList[]> {
  try {
    const { data } = await apiClient.get<{ lists: SavedList[] }>('/lists');
    return data.lists ?? [];
  } catch (error) {
    throw refusal(error);
  }
}

/** One list, with its records. */
export async function fetchSavedList(id: string): Promise<SavedListDetail> {
  try {
    const { data } = await apiClient.get<SavedListDetail>(`/lists/${encodeURIComponent(id)}`);
    return { ...data, records: data.records ?? [] };
  } catch (error) {
    throw refusal(error);
  }
}

/**
 * Make a list.
 *
 * The scope is sent only when it is the wider one. Personal is the server's
 * default, and a request that says so explicitly and one that omits it produce
 * the same list — so omitting keeps the wire honest about what was chosen.
 */
export async function createSavedList(name: string, scope: ListScope): Promise<SavedList> {
  try {
    const { data } = await apiClient.post<SavedList>('/lists', {
      name,
      ...(scope === 'organisation' ? { scope } : {})
    });
    return data;
  } catch (error) {
    throw refusal(error);
  }
}

/**
 * Rename a list, or change who can see it.
 *
 * Both at once because the server takes both and requires both: PATCH with a
 * name and no scope would be read as a scope of `""`, which is invalid.
 */
export async function updateSavedList(
  id: string,
  name: string,
  scope: ListScope
): Promise<SavedList> {
  try {
    const { data } = await apiClient.patch<SavedList>(`/lists/${encodeURIComponent(id)}`, {
      name,
      scope
    });
    return data;
  } catch (error) {
    throw refusal(error);
  }
}

/** Delete a list and everything in it. */
export async function deleteSavedList(id: string): Promise<void> {
  try {
    await apiClient.delete(`/lists/${encodeURIComponent(id)}`);
  } catch (error) {
    throw refusal(error);
  }
}

/**
 * Put records into a list, in as many requests as it takes.
 *
 * # The batching is here so that no screen has to know the cap
 *
 * The server takes a thousand per request and a selection may hold thirty
 * thousand. A caller that had to batch would be a caller that could forget to,
 * and the failure is a request refused for a reason the customer cannot act
 * on.
 *
 * # A failed batch stops the run and says what got through
 *
 * The alternative — carrying on past a failure — produces a list whose contents
 * nobody can predict, and a total that is right about a run that half happened.
 * Stopping and reporting `added` is recoverable: adding the same records again
 * is not an error, because the list is keyed on who they are.
 */
export async function addRecordsToList(
  id: string,
  records: SavedListRecordInput[]
): Promise<AddRecordsOutcome> {
  const outcome: AddRecordsOutcome = { added: 0, requested: 0 };

  for (let from = 0; from < records.length; from += maxRecordsPerRequest) {
    const batch = records.slice(from, from + maxRecordsPerRequest);
    try {
      const { data } = await apiClient.post<AddRecordsOutcome>(
        `/lists/${encodeURIComponent(id)}/records`,
        { records: batch }
      );
      outcome.added += data.added ?? 0;
      outcome.requested += data.requested ?? batch.length;
    } catch (error) {
      // What got in stays in. The caller is told how far it reached so it can
      // say so rather than reporting a total failure that did not happen.
      throw new PartialAdd(outcome.added, records.length, refusal(error));
    }
  }

  return outcome;
}

/** An add that stopped part-way, and how far it reached. */
export class PartialAdd extends Error {
  constructor(
    readonly added: number,
    readonly requested: number,
    readonly cause: SavedListRefused
  ) {
    super(cause.message);
    this.name = 'PartialAdd';
  }
}

/** Take records out of a list, batched as adding is. */
export async function removeRecordsFromList(id: string, recordIDs: string[]): Promise<number> {
  let removed = 0;

  for (let from = 0; from < recordIDs.length; from += maxRecordsPerRequest) {
    const batch = recordIDs.slice(from, from + maxRecordsPerRequest);
    try {
      const { data } = await apiClient.delete<{ removed: number }>(
        `/lists/${encodeURIComponent(id)}/records`,
        { record_ids: batch }
      );
      removed += data.removed ?? 0;
    } catch (error) {
      throw refusal(error);
    }
  }

  return removed;
}

/** Turn a rejected request into something with a code on it. */
function refusal(error: unknown): SavedListRefused {
  if (error instanceof ApiError) {
    const body = error.data as { code?: string; error?: string } | undefined;
    if (body?.code) {
      return new SavedListRefused(
        body.code,
        body.error ?? 'Die Liste konnte nicht bearbeitet werden.'
      );
    }
  }
  return new SavedListRefused('LIST_FAILED', 'Die Liste konnte nicht bearbeitet werden.');
}
